import { UserProfile } from 'firebase/auth';
import Link from 'next/link';
import { formatMoney, possessive } from 'sdk';
import { MasterCode } from 'types';
import AppStoreButtonSVG from '~/assets/svg/app-store-button';
import GooglePlayButtonSVG from '~/assets/svg/google-play-button';
import BookInHandSVG from '~/assets/svg/icons/book-in-hand';
import SaveMoneySVG from '~/assets/svg/icons/save-money';
import JoinCommunitySVG from '~/assets/svg/icons/join-community';
import AppReviewCard from '~/components/marketing/landing-page/review-card';
import PageMetaData from '~/components/page-meta-data';
import Links from '~/lib/helpers/link-helper';
import styles from '~/styles/pages/promo.module.scss';
import StarSVG from '~/assets/svg/icons/star';
import UserProfileImage from '../components/user-profile-image';
import COLORS from '~/lib/helpers/color-helper';
import InfoIconContent from '~/components/info-icon';

interface Props {
  promo: MasterCode & { sellerData?: UserProfile } & { id: string };
}
export default function PromoLayout({ promo }: Props) {
  function renderAmbassador() {
    if (!promo?.sellerData) return null;
    return (
      <div className={styles['ambassador']}>
        <UserProfileImage user={promo.sellerData} link />
      </div>
    );
  }

  return (
    <>
      <PageMetaData title={`Pangobooks - ${promo.code_id}`} />
      <header className={styles.header}>
        <div className={styles['header-content-container']}>
          <h1 className={styles['promo-text']}>
            {promo.sellerData?.name
              ? ` Welcome ${possessive(promo.sellerData.name as string)} visitors!`
              : 'Thanks for visiting PangoBooks'}
          </h1>
        </div>
      </header>

      <div className={styles['promo-container']}>
        <div className={styles['promo-bubble']}>
          {renderAmbassador()}
          <div className={styles['promo-bubble-text']}>
            Enjoy <span style={{ color: COLORS.primary }}>{formatMoney(promo.amount)} off</span> your first purchase
            when you use {promo.sellerData?.name ? ` ${possessive(promo.sellerData.name as string)}` : ''} code{' '}
            <span style={{ color: COLORS.primary }}>"{promo.code_id}"</span> at checkout.
          </div>
        </div>
      </div>

      <div className={styles['body-container']}>
        <div className={styles['body-container-inner']}>
          <h2 className={styles['header-text']}>Join thousands of people on the app</h2>
          <div className={styles['ratings-div']}>
            <div className={styles['avg-rating-div']}>
              4.9
              <div className={styles['title-star']} style={{}}>
                {[...Array(1)].map((i, index) => (
                  <StarSVG strokeColor={COLORS.yellow} key={index} />
                ))}
              </div>
              average app ratings
            </div>
            <div className={styles['vertical-line']}></div>
            <h3>9.1K ratings</h3>
            <div className={styles['vertical-line']}></div>
            <h3>100K+ downloads</h3>
          </div>

          <div className={styles['app-download']}>
            <Link href={Links.appStore.apple} passHref>
              <AppStoreButtonSVG />
            </Link>
            <Link href={Links.appStore.google} passHref>
              <GooglePlayButtonSVG />
            </Link>
          </div>
        </div>

        <AppReviewCard
          titles={['Amazing! Easiest listing I’ve ever made!', 'Better than the rest!', 'Fantastic Platform!']}
          reviews={[
            ` Not only does this app have great menuing and interface, the listing is a piece of cake! I don’t think I’ve ever 
            seen an app with a better, easier, and faster method of listing items. And even better…. ITS ALL FOR BOOKS. If you’re a bookworm, 
            download this app right now! It’s so simple to sell books you no longer want, and to buy books that you’ve been dying to read!`,
            `I am an avid EBay and Poshmark seller, yet always struggled to sell books on those platforms due to the limitations of their 
            descriptors/variety of other listings. I also always hated to donate books or sell to Half-Price Books (or others) because I was only 
            making cents back on books I paid good money for. PangoBooks was the solution I was looking for! It’s really easy to list items. I particularly 
            enjoy the option to scan the barcode and have PangoBooks fill in details about the book you’re listing. Selling is a breeze as well; once someone 
            purchases from you, the label is available in multiple ways. I am very pleased with this process. I haven’t purchased anything yet, but there are 
            tons of great options, including many new releases and harder to find classic editions of books.`,
            `I’ve used most of the buy/sell book platforms - Thriftbooks, AbeBooks, EBay, Amazon, SecondSale, etc and I have to say this is my new favorite! 
            I’ve only been using it for a few weeks but I’ve found some real gems for a great price! Plus the seller interaction is refreshing! They also sent 
            out a promo code for joining and as well as a promo code for listing books on their platform; literally they are the best!!`,
          ]}
        />
      </div>

      <div className={styles['icon-container']}>
        <InfoIconContent
          title="Earn money selling books"
          content={['We’ve simplified the whole process to a few quick and easy steps.']}
          icon={<BookInHandSVG marginLeft="1rem" />}
        />

        <InfoIconContent
          title="Save big buying from other readers"
          content={['Pango sellers offer unbeatable prices and all sales are 100% guaranteed!']}
          icon={<SaveMoneySVG marginLeft="2rem" />}
        />

        <InfoIconContent
          title="Join our amazing community"
          content={['Connect with other passionate readers and discover a new world of amazing reads!']}
          icon={<JoinCommunitySVG marginTop="0.8rem" />}
        />
      </div>
    </>
  );
}
